const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const EMPTY_ADMIN_CONFIG = {
  default_mapping_active: false,
  default_budget_active: false,
  default_mapping_filename: null,
  default_budget_filename: null,
};

async function readApiError(response, fallback) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    const error = await response.json();
    const detail = error.detail;
    if (detail?.errors) {
      return detail.errors.join(", ");
    }
    return detail || error.message || fallback;
  }

  const text = await response.text();
  if (response.status === 502) {
    return "The backend gateway timed out or is unavailable. Please try again; if it continues, restart/check the backend server.";
  }

  return text ? `${fallback}: ${text.slice(0, 180)}` : fallback;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getAuthHeaders(headers = {}) {
  const token = localStorage.getItem("token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

export async function getRevenueForecast(params = {}) {
  const queryParts = [];
  if (params.target_month != null) queryParts.push(`target_month=${encodeURIComponent(params.target_month)}`);
  if (params.target_year != null) queryParts.push(`target_year=${encodeURIComponent(params.target_year)}`);
  if (params.target_period) queryParts.push(`target_period=${encodeURIComponent(params.target_period)}`);
  if (params.horizon != null) queryParts.push(`horizon=${encodeURIComponent(params.horizon)}`);

  const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  const response = await fetch(`${API_BASE}/forecast/revenue${queryString}`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load revenue forecast");
}


async function parseResponseError(response, defaultMessage = "Request failed") {
  let errorMsg = `${defaultMessage} (${response.status})`;
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      errorMsg = errorData.detail?.errors?.join(", ") || errorData.detail || errorData.message || errorMsg;
    } else {
      const text = await response.text();
      if (response.status === 502) {
        errorMsg = "502 Bad Gateway: The backend server is down or unreachable.";
      } else if (response.status === 504) {
        errorMsg = "504 Gateway Timeout: Request timed out on backend server.";
      } else if (response.status === 413) {
        errorMsg = "File is too large. Server file size limit exceeded.";
      } else if (text && text.length < 200 && !text.includes("<html")) {
        errorMsg = text;
      }
    }
  } catch (e) {
    if (response.status === 502) {
      errorMsg = "502 Bad Gateway: Backend server unreachable.";
    }
  }
  return new Error(errorMsg);
}

async function safeJsonResponse(response, defaultMsg = "Invalid response from server") {
  if (!response.ok) {
    throw await parseResponseError(response, defaultMsg);
  }
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    if (text.trim().startsWith("<") || text.includes("<html")) {
      throw new Error(`Server returned HTML instead of JSON (${response.status}). Check backend service URL & proxy routing.`);
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(text || `${defaultMsg} (${response.status})`);
    }
  }
  return response.json();
}

// Upload & Report Generation
export async function uploadFiles(files) {
  const formData = new FormData();
  formData.append("tb_current", files.tb_current);
  formData.append("tb_previous", files.tb_previous);
  if (files.budget) {
    formData.append("budget", files.budget);
  }
  if (files.mapping) {
    formData.append("mapping", files.mapping);
  }

  const response = await fetch(`${API_BASE}/upload`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Upload failed"));
  }

  return response.json();
}

async function validateWorkbookFile(path, file, fallback) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/${path}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, fallback));
  }

  return response.json();
}

export function validateBudgetFile(file) {
  return validateWorkbookFile("validate-budget", file, "Budget validation failed");
}

export function validateMappingFile(file) {
  return validateWorkbookFile("validate-mapping", file, "Mapping validation failed");
}

export async function uploadPLFile(file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/upload-pl`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "PL upload failed"));
  }

  return response.json();
}

export async function getPLRevenue(periodMonth, periodYear) {
  const queryParts = [];
  if (periodMonth) queryParts.push(`period_month=${encodeURIComponent(periodMonth)}`);
  if (periodYear) queryParts.push(`period_year=${encodeURIComponent(periodYear)}`);
  const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";

  const response = await fetch(`${API_BASE}/pl-revenue${queryString}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch PL revenue"));
  }

  return response.json();
}

export async function generateReport(sessionId) {
  const response = await fetch(`${API_BASE}/generate?session_id=${sessionId}`, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Report generation failed"));
  }

  const result = await response.json();
  if (result.status !== "processing") {
    return result;
  }

  return pollReportStatus(sessionId);
}

export async function pollReportStatus(sessionId) {
  for (let attempt = 0; attempt < 120; attempt += 1) {
    await wait(2000);

    const response = await fetch(`${API_BASE}/report-status/${sessionId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to fetch report status"));
    }

    const result = await response.json();
    if (result.status === "success") {
      return result;
    }
    if (result.status === "error") {
      throw new Error(result.message || "Report generation failed");
    }
  }

  throw new Error("Report generation is taking longer than expected. Please try again later.");
}

export function getDownloadUrl(filename) {
  return `${API_BASE}/download/${filename}`;
}

export async function generateUnmappedReport(sessionId) {
  const response = await fetch(`${API_BASE}/generate-unmapped?session_id=${sessionId}`, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Unmapped report generation failed"));
  }

  const result = await response.json();
  if (result.status === "success") {
    return result;
  }
  if (result.status === "error") {
    throw new Error(result.message || "Unmapped report generation failed");
  }

  // status === "processing" — poll until done
  return pollUnmappedStatus(sessionId);
}

export async function pollUnmappedStatus(sessionId) {
  for (let attempt = 0; attempt < 200; attempt += 1) {
    await wait(3000);

    const response = await fetch(`${API_BASE}/unmapped-status/${sessionId}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(await readApiError(response, "Failed to fetch unmapped report status"));
    }

    const result = await response.json();
    if (result.status === "success") {
      return result;
    }
    if (result.status === "error") {
      throw new Error(result.message || "Unmapped report generation failed");
    }
  }

  throw new Error("Unmapped report is taking longer than expected. Please try again later.");
}

export function getUnmappedDownloadUrl(filename) {
  return `${API_BASE}/download-unmapped/${filename}`;
}

// Admin Services (Templates & Flexfields)
export async function getAdminConfig() {
  const response = await fetch(`${API_BASE}/admin/config`, {
    headers: getAuthHeaders(),
  });
  if (response.status === 404) {
    return EMPTY_ADMIN_CONFIG;
  }
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch admin config"));
  }
  return response.json();
}

export async function uploadDefaultFile(fileType, file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/admin/upload-default?file_type=${fileType}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Default upload failed"));
  }
  return response.json();
}

export async function getSessions() {
  const response = await fetch(`${API_BASE}/admin/sessions`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch sessions history"));
  }
  return response.json();
}

export async function getFlexfields() {
  const response = await fetch(`${API_BASE}/admin/flexfields`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch flexfields configuration"));
  }
  return response.json();
}

export async function updateFlexfields(flexfields) {
  const response = await fetch(`${API_BASE}/admin/flexfields`, {
    method: "POST",
    headers: getAuthHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(flexfields),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to update flexfields"));
  }
  return response.json();
}

// AUTH SERVICES
export async function authRegister(fullName, email, password, confirmPassword, role) {
  const response = await fetch(`${API_BASE}/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      full_name: fullName,
      email,
      password,
      confirm_password: confirmPassword,
      role,
    }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Registration failed"));
  }
  return response.json();
}

export async function authLogin(email, password) {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Login failed"));
  }
  return response.json();
}

export async function requestPasswordResetOtp(email) {
  const response = await fetch(`${API_BASE}/auth/forgot-password/request-otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to send OTP"));
  }
  return response.json();
}

export async function verifyPasswordResetOtp(email, otp) {
  const response = await fetch(`${API_BASE}/auth/forgot-password/verify-otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, otp }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Invalid OTP"));
  }
  return response.json();
}

export async function resetForgottenPassword(email, otp, newPassword, confirmPassword) {
  const response = await fetch(`${API_BASE}/auth/forgot-password/reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      email,
      otp,
      new_password: newPassword,
      confirm_password: confirmPassword,
    }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to reset password"));
  }
  return response.json();
}

export async function authAzureLogin(idToken) {
  const response = await fetch(`${API_BASE}/auth/azure-login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ id_token: idToken }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Azure login failed"));
  }
  return response.json();
}

// ── Microsoft PKCE SSO ────────────────────────────────────────────────────

/**
 * Step 1 — Ask backend for the Microsoft authorization URL (with PKCE + encrypted state).
 * Returns { auth_url: "https://login.microsoftonline.com/..." }
 */
export async function getMicrosoftLoginUrl() {
  const response = await fetch(`${API_BASE}/auth/microsoft/login`);
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to get Microsoft login URL"));
  }
  return response.json();
}

/**
 * Step 2 — After Microsoft redirects back to /auth/callback?code=&state=,
 * send code + state to backend to complete the PKCE exchange.
 * Returns { access_token, token_type, sso_status, user }
 */
export async function authMicrosoftFinish(code, state) {
  const response = await fetch(`${API_BASE}/auth/microsoft/finish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, state }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Microsoft SSO login failed"));
  }
  return response.json();
}

/**
 * Step 3 — Registers new SSO user with the role selected in the UI.
 * Returns { status, message }
 */
export async function authMicrosoftRegister(microsoftId, email, fullName, serviceNumber, role) {
  const response = await fetch(`${API_BASE}/auth/microsoft/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      microsoft_id: microsoftId,
      email,
      full_name: fullName,
      service_number: serviceNumber,
      role,
    }),
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, "Microsoft SSO registration failed"));
  }
  return response.json();
}

export async function authLogout() {
  const response = await fetch(`${API_BASE}/auth/logout`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Logout failed"));
  }
  return response.json();
}

export async function authMe() {
  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch current user profile"));
  }
  return response.json();
}

// USER MANAGEMENT SERVICES
export async function getUsers() {
  const response = await fetch(`${API_BASE}/users`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch users"));
  }
  return response.json();
}

export async function getUser(id) {
  const response = await fetch(`${API_BASE}/users/${id}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, `Failed to fetch user with ID ${id}`));
  }
  return response.json();
}

export async function createUser(user) {
  const response = await fetch(`${API_BASE}/users`, {
    method: "POST",
    headers: getAuthHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(user),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to create user"));
  }
  return response.json();
}

export async function updateUser(id, userData) {
  const response = await fetch(`${API_BASE}/users/${id}`, {
    method: "PUT",
    headers: getAuthHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify(userData),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to update user"));
  }
  return response.json();
}

export async function deleteUser(id) {
  const response = await fetch(`${API_BASE}/users/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to delete user"));
  }
  return response.json();
}

export async function approveUser(id) {
  const response = await fetch(`${API_BASE}/users/${id}/approve`, {
    method: "PATCH",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to approve user"));
  }
  return response.json();
}

export async function rejectUser(id) {
  const response = await fetch(`${API_BASE}/users/${id}/reject`, {
    method: "PATCH",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to reject user"));
  }
  return response.json();
}

export async function activateUser(id) {
  const response = await fetch(`${API_BASE}/users/${id}/activate`, {
    method: "PATCH",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to activate user"));
  }
  return response.json();
}

export async function deactivateUser(id) {
  const response = await fetch(`${API_BASE}/users/${id}/deactivate`, {
    method: "PATCH",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to deactivate user"));
  }
  return response.json();
}

export async function changeUserRole(id, role) {
  const response = await fetch(`${API_BASE}/users/${id}/role`, {
    method: "PATCH",
    headers: getAuthHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ role }),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to change role"));
  }
  return response.json();
}

export async function resetUserPassword(id, newPassword) {
  const response = await fetch(`${API_BASE}/users/${id}/reset-password`, {
    method: "PATCH",
    headers: getAuthHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({ new_password: newPassword }),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to reset password"));
  }
  return response.json();
}

// AUDIT LOG SERVICES
export async function getSystemAuditLogs(params = {}) {
  const queryParts = [];
  if (params.skip !== undefined) queryParts.push(`skip=${params.skip}`);
  if (params.limit !== undefined) queryParts.push(`limit=${params.limit}`);
  if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
  if (params.user_id) queryParts.push(`user_id=${params.user_id}`);
  if (params.action) queryParts.push(`action=${encodeURIComponent(params.action)}`);
  if (params.module) queryParts.push(`module=${encodeURIComponent(params.module)}`);
  if (params.start_date) queryParts.push(`start_date=${params.start_date}`);
  if (params.end_date) queryParts.push(`end_date=${params.end_date}`);

  const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  const response = await fetch(`${API_BASE}/audit-logs${queryString}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, "Failed to fetch audit logs"));
  }
  return response.json();
}

// ANOMALY & FRAUD DETECTION SERVICES
export async function getAnomalySummary(params = {}) {
  const queryParts = [];
  const yr = params.year != null ? params.year : 2026;
  queryParts.push(`year=${encodeURIComponent(yr)}`);
  if (params.period_month) queryParts.push(`period_month=${encodeURIComponent(params.period_month)}`);
  if (params.revenue_category) queryParts.push(`revenue_category=${encodeURIComponent(params.revenue_category)}`);

  const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  const response = await fetch(`${API_BASE}/anomaly/summary${queryString}`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load anomaly summary");
}

export async function getAnomalyMatrix(params = {}) {
  const queryParts = [];
  const yr = params.year != null ? params.year : 2026;
  queryParts.push(`year=${encodeURIComponent(yr)}`);

  const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  const response = await fetch(`${API_BASE}/anomaly/matrix${queryString}`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load category x month anomaly matrix");
}

export async function getAnomalyResults(params = {}) {
  const queryParts = [];
  const yr = params.year != null ? params.year : 2026;
  queryParts.push(`year=${encodeURIComponent(yr)}`);
  if (params.risk_level) queryParts.push(`risk_level=${encodeURIComponent(params.risk_level)}`);
  if (params.period_month) queryParts.push(`period_month=${encodeURIComponent(params.period_month)}`);
  if (params.revenue_category) queryParts.push(`revenue_category=${encodeURIComponent(params.revenue_category)}`);
  if (params.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
  if (params.skip != null) queryParts.push(`skip=${params.skip}`);
  if (params.limit != null) queryParts.push(`limit=${params.limit}`);
  if (params.sort_by) queryParts.push(`sort_by=${encodeURIComponent(params.sort_by)}`);
  if (params.sort_order) queryParts.push(`sort_order=${encodeURIComponent(params.sort_order)}`);

  const queryString = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
  const response = await fetch(`${API_BASE}/anomaly/results${queryString}`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load anomaly records");
}

export async function getAnomalyFilterOptions() {
  const response = await fetch(`${API_BASE}/anomaly/filters`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load anomaly filter options");
}

export async function triggerAnomalyAnalysis() {
  const response = await fetch(`${API_BASE}/anomaly/analyze`, {
    method: "POST",
    headers: getAuthHeaders({
      "Content-Type": "application/json",
    }),
  });
  return safeJsonResponse(response, "Failed to trigger anomaly detection analysis");
}

// Use case access
export async function getMyUseCases() {
  const response = await fetch(`${API_BASE}/use-cases/me`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load your use cases");
}

export async function getUseCaseAssignments() {
  const response = await fetch(`${API_BASE}/use-cases/assignments`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load use case assignments");
}

export async function updateUseCaseAssignment(email, useCases) {
  const response = await fetch(`${API_BASE}/use-cases/assignments`, {
    method: "PUT",
    headers: getAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ email, use_cases: useCases }),
  });
  return safeJsonResponse(response, "Failed to save use case assignment");
}

// Finance Board Pack
export async function generateBoardPack(file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/board-pack/generate`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });
  return safeJsonResponse(response, "Failed to generate the board pack");
}

export async function downloadBoardPack(filename) {
  const response = await fetch(`${API_BASE}/board-pack/download/${encodeURIComponent(filename)}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw await parseResponseError(response, "Failed to download the board pack");
  }

  const blob = await response.blob();
  const localUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = localUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(localUrl);
}

// Financial Analysis Report
export async function getFinancialAnalysisStatus() {
  const response = await fetch(`${API_BASE}/financial-analysis/status`, {
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Unable to load the trial balances");
}

export async function uploadTrialBalances(files) {
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));

  const response = await fetch(`${API_BASE}/financial-analysis/trial-balances`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });
  return safeJsonResponse(response, "Failed to upload the trial balances");
}

export async function deleteTrialBalance(year, month) {
  const response = await fetch(`${API_BASE}/financial-analysis/trial-balances/${year}/${month}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Failed to remove the trial balance");
}

export async function uploadGlMapping(file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API_BASE}/financial-analysis/mapping`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });
  return safeJsonResponse(response, "Failed to upload the GL code mapping");
}

export async function generateFinancialAnalysis(year, quarter) {
  const response = await fetch(`${API_BASE}/financial-analysis/generate`, {
    method: "POST",
    headers: getAuthHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ year, quarter }),
  });
  return safeJsonResponse(response, "Failed to generate the report");
}

export async function downloadFinancialAnalysis(filename) {
  const response = await fetch(`${API_BASE}/financial-analysis/download/${encodeURIComponent(filename)}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw await parseResponseError(response, "Failed to download the report");
  }

  const blob = await response.blob();
  const localUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = localUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(localUrl);
}

export async function uploadFABudgets(files, ytdCumulate = false) {
  const formData = new FormData();
  const fileList = Array.isArray(files) ? files : [files];
  for (const f of fileList) {
    formData.append("files", f);
  }
  const url = `${API_BASE}/financial-analysis/budget${ytdCumulate ? "?ytd_cumulate=true" : ""}`;
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: formData,
  });
  return safeJsonResponse(response, "Failed to upload budget workbooks");
}

export const uploadFABudget = uploadFABudgets;

export async function deleteFABudget(year, month) {
  const url = year && month
    ? `${API_BASE}/financial-analysis/budget/${year}/${month}`
    : `${API_BASE}/financial-analysis/budget`;
  const response = await fetch(url, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return safeJsonResponse(response, "Failed to remove the budget workbook");
}

